/**
 * Performance Calculations
 *
 * Core module for calculating:
 * - Random IOPS (read, write, effective)
 * - Sequential throughput
 * - Write penalties by RAID level
 *
 * Reference: DESIGN.md §8, Math Verification Plan §2
 * Test Cases: Math Verification Plan §3 (Test Cases 2, 5)
 *
 * CRITICAL AXIOM: The Single-Disk Rule
 * Vdev Random IOPS ≈ IOPS_single_disk
 *
 * For RAID-Z vdevs: IOPS scales with vdev count, NOT disk count within vdev
 * For mirror vdevs: IOPS scales with vdev count, reads can split across members
 */

import type {
  StorageTopology,
  DriveParameters,
  PerformanceInputs,
  PerformanceResults,
  RaidLevelMeta,
} from '../types';
import { RAID_LEVEL_META } from './capacity';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Default IOPS for 7.2k RPM HDDs */
export const DEFAULT_IOPS_7200RPM = 90;

/** Default IOPS for 15k RPM HDDs */
export const DEFAULT_IOPS_15KRPM = 180;

/** Default IOPS for SATA/SAS SSDs */
export const DEFAULT_IOPS_SSD = 10000;

/** Default sequential throughput for HDDs (MB/s) */
export const DEFAULT_SEQUENTIAL_HDD_MBPS = 200;

/** Default sequential throughput for SSDs (MB/s) */
export const DEFAULT_SEQUENTIAL_SSD_MBPS = 500;

// ============================================================================
// IOPS CALCULATIONS
// ============================================================================

/**
 * Get default IOPS for drive type
 *
 * @param driveSizeTB - Drive size (used to infer enterprise/consumer)
 * @param driveIOPS - User-provided IOPS (optional)
 * @returns IOPS value
 */
export function getDriveIOPS(driveSizeTB: number, driveIOPS?: number): number {
  if (driveIOPS !== undefined) {
    return driveIOPS;
  }

  // Heuristic: Larger drives are typically enterprise grade with higher IOPS
  if (driveSizeTB >= 10) {
    return DEFAULT_IOPS_7200RPM; // 7.2k RPM enterprise
  }
  return DEFAULT_IOPS_7200RPM;
}

/**
 * Calculate effective IOPS for pool
 *
 * Formula: I_eff = (V × I_disk) / (Read% + (Penalty × Write%))
 *
 * CRITICAL: V is vdev count, NOT disk count for RAIDZ
 * - RAIDZ: Each vdev ≈ 1 disk's IOPS (Single-Disk Rule)
 * - Mirrors: Each vdev can split reads across members
 *
 * Test Case Reference: Math Verification Plan §3, Test Case 2
 * - 6-disk RAIDZ2, 90 IOPS, 100% read → ~90 IOPS (NOT 540)
 *
 * Test Case Reference: Math Verification Plan §3, Test Case 5
 * - 12-disk RAIDZ2, 90 IOPS, 70% read → ~36 IOPS
 * - 6× 2-way mirrors, 90 IOPS, 70% read → ~415 IOPS
 *
 * @param topology - Storage topology
 * @param diskIOPS - Base IOPS per drive
 * @param readRatio - Percentage of read operations (0-1)
 * @param writeRatio - Percentage of write operations (0-1)
 * @returns Effective IOPS for pool
 */
export function calculateEffectiveIOPS(
  topology: StorageTopology,
  diskIOPS: number,
  readRatio: number,
  writeRatio: number
): number {
  const { raidLevel, vdevCount } = topology;
  const meta = RAID_LEVEL_META[raidLevel] as RaidLevelMeta;

  // For RAIDZ: vdev count (NOT disk count)
  // For mirrors: vdev count
  const vdevIOPS = vdevCount * diskIOPS;

  // Formula: I_eff = (V × I_disk) / (Read% + (Penalty × Write%))
  const effectiveIOPS = vdevIOPS / (readRatio + meta.writePenalty * writeRatio);

  return effectiveIOPS;
}

/**
 * Calculate random read IOPS for pool
 *
 * For RAIDZ: Read IOPS = vdev_count × disk_IOPS (Single-Disk Rule)
 * For mirrors: Read IOPS = vdev_count × disk_IOPS × mirror_width (reads split)
 *
 * @param topology - Storage topology
 * @param diskIOPS - Base IOPS per drive
 * @returns Random read IOPS
 */
export function calculateRandomReadIOPS(
  topology: StorageTopology,
  diskIOPS: number
): number {
  const { raidLevel, vdevCount, drivesPerVdev } = topology;

  if (raidLevel === 'mirror') {
    // Mirrors can split reads across all members
    return vdevCount * drivesPerVdev * diskIOPS;
  }

  // RAIDZ: Single-Disk Rule
  return vdevCount * diskIOPS;
}

/**
 * Calculate random write IOPS for pool
 *
 * For all vdev types: Write IOPS = vdev_count × disk_IOPS
 * (Mirrors and RAIDZ both limited by single disk write IOPS)
 *
 * @param topology - Storage topology
 * @param diskIOPS - Base IOPS per drive
 * @returns Random write IOPS
 */
export function calculateRandomWriteIOPS(
  topology: StorageTopology,
  diskIOPS: number
): number {
  const { vdevCount } = topology;
  return vdevCount * diskIOPS;
}

// ============================================================================
// THROUGHPUT CALCULATIONS
// ============================================================================

/**
 * Calculate sequential read throughput
 *
 * Formula: Throughput_vdev = N_disks × S_disk
 * Throughput_pool = Σ Throughput_vdev
 *
 * For RAIDZ: All disks participate
 * For mirrors: Read from all disks (can split reads)
 *
 * @param topology - Storage topology
 * @param sequentialMBps - Sequential speed per disk (MB/s)
 * @returns Sequential read throughput in MB/s
 */
export function calculateSequentialReadThroughput(
  topology: StorageTopology,
  sequentialMBps: number
): number {
  const { raidLevel, vdevCount, drivesPerVdev } = topology;

  if (raidLevel === 'mirror') {
    // Mirrors can read from all disks
    return vdevCount * drivesPerVdev * sequentialMBps;
  }

  // RAIDZ: All data disks participate
  const meta = RAID_LEVEL_META[raidLevel] as RaidLevelMeta;
  const dataDisks = drivesPerVdev - meta.parityDrives;
  return vdevCount * dataDisks * sequentialMBps;
}

/**
 * Calculate sequential write throughput
 *
 * CRITICAL: ZFS Copy-on-Write (CoW) limits write parallelism
 *
 * For mirrors: Limited to single disk speed (must write same data to all members)
 * For RAIDZ: Each block write goes to one disk, so ~1-2× single disk speed
 *
 * ZFS CoW means:
 * - Each write goes to a new location (one disk at a time)
 * - Write allocator rotates through disks, but can't stripe like traditional RAID
 * - Real-world: Wide vdevs get ~1.5-2× speed, NOT N× like traditional RAID 0
 *
 * @param topology - Storage topology
 * @param sequentialMBps - Sequential speed per disk (MB/s)
 * @returns Sequential write throughput in MB/s
 */
export function calculateSequentialWriteThroughput(
  topology: StorageTopology,
  sequentialMBps: number
): number {
  const { raidLevel, vdevCount, drivesPerVdev } = topology;

  if (raidLevel === 'mirror') {
    // Mirrors: Write same data to all members, limited to single disk speed
    // Multiple vdevs can write in parallel, so vdevCount × 1× speed
    return vdevCount * sequentialMBps;
  }

  // RAIDZ: CoW means writes go to one disk at a time per vdev
  // Wide vdevs get modest benefit (~1.5-2×), not N×
  // Model this as: vdevCount × (1 + log2(drivesPerVdev) / 2) × diskSpeed
  const cowMultiplier = 1 + Math.log2(drivesPerVdev) / 2;
  return vdevCount * cowMultiplier * sequentialMBps;
}

// ============================================================================
// BOTTLENECK IDENTIFICATION
// ============================================================================

/**
 * Identify performance bottleneck
 *
 * @param topology - Storage topology
 * @param readRatio - Percentage of read operations
 * @returns Bottleneck description
 */
export function identifyBottleneck(
  topology: StorageTopology,
  readRatio: number
): string {
  const { raidLevel, drivesPerVdev } = topology;

  if (raidLevel !== 'mirror' && drivesPerVdev > 8) {
    return 'Wide RAIDZ vdev - consider more vdevs with fewer disks for better IOPS';
  }

  if (raidLevel.startsWith('raidz') && readRatio < 0.5) {
    return 'RAIDZ write penalty - consider mirrors for write-heavy workloads';
  }

  if (raidLevel === 'mirror' && drivesPerVdev > 2) {
    return 'Wide mirrors have diminishing returns - 2-way mirrors are most efficient';
  }

  return 'No significant bottleneck detected';
}

// ============================================================================
// MAIN PERFORMANCE CALCULATION ORCHESTRATOR
// ============================================================================

/**
 * Calculate complete performance assessment
 *
 * Orchestrates all performance calculations:
 * 1. Random IOPS (read, write, effective)
 * 2. Sequential throughput
 * 3. Bottleneck identification
 *
 * @param topology - Storage topology
 * @param drive - Drive parameters
 * @param performance - Performance inputs
 * @returns Complete performance assessment
 */
export function calculatePerformance(
  topology: StorageTopology,
  drive: DriveParameters,
  performance: PerformanceInputs
): PerformanceResults {
  const { raidLevel } = topology;
  const meta = RAID_LEVEL_META[raidLevel] as RaidLevelMeta;

  // Get IOPS (use provided or default)
  const diskIOPS = getDriveIOPS(drive.driveSizeTB, drive.driveIOPS);
  const sequentialMBps = drive.peakSequentialMBps;

  // Calculate IOPS
  const randomReadIOPS = calculateRandomReadIOPS(topology, diskIOPS);
  const randomWriteIOPS = calculateRandomWriteIOPS(topology, diskIOPS);
  const effectiveIOPS = calculateEffectiveIOPS(
    topology,
    diskIOPS,
    performance.readPercentage,
    1 - performance.readPercentage
  );

  // Calculate throughput
  const sequentialReadMBps = calculateSequentialReadThroughput(
    topology,
    sequentialMBps
  );
  const sequentialWriteMBps = calculateSequentialWriteThroughput(
    topology,
    sequentialMBps
  );

  // Identify bottleneck
  const bottleneck = identifyBottleneck(topology, performance.readPercentage);

  return {
    randomReadIOPS,
    randomWriteIOPS,
    effectiveIOPS,
    sequentialReadMBps,
    sequentialWriteMBps,
    writePenalty: meta.writePenalty,
    bottleneck,
  };
}
